import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  GraduationCap, Briefcase, Rocket, TrendingUp, ArrowRight, ArrowLeft,
  Calendar, MapPin, Users, Clock, Award, Star, Play, CheckCircle,
  ChevronRight, Share2, BookOpen, Target, Zap, Building2, ExternalLink,
  Copy, Check, AlertCircle, Loader2, Tag, X
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper component for formatted text rendering
const FormattedText = ({ text, className = "" }) => {
  if (!text) return null;
  
  // Split by double newlines for paragraphs, single newlines for line breaks
  const paragraphs = text.split(/\n\n+/);
  
  return (
    <div className={`space-y-4 ${className}`}>
      {paragraphs.map((paragraph, pIdx) => {
        const lines = paragraph.split('\n');
        
        // Check if this paragraph is a list (bullet or numbered)
        const isBulletList = lines.every(line => 
          !line.trim() || /^[\•\-\*\→\▶]\s/.test(line.trim())
        );
        const isNumberedList = lines.every(line => 
          !line.trim() || /^\d+[\.\)]\s/.test(line.trim())
        );
        
        if (isBulletList && lines.some(l => l.trim())) {
          return (
            <ul key={pIdx} className="space-y-2 pl-1">
              {lines.filter(l => l.trim()).map((line, lIdx) => (
                <li key={lIdx} className="flex items-start gap-3 text-gray-300">
                  <span className="text-cyan-400 mt-1.5 flex-shrink-0">•</span>
                  <span>{line.replace(/^[\•\-\*\→\▶]\s*/, '')}</span>
                </li>
              ))}
            </ul>
          );
        }
        
        if (isNumberedList && lines.some(l => l.trim())) {
          return (
            <ol className="space-y-2 pl-1" key={pIdx}>
              {lines.filter(l => l.trim()).map((line, lIdx) => (
                <li key={lIdx} className="flex items-start gap-3 text-gray-300">
                  <span className="text-purple-400 font-semibold flex-shrink-0 min-w-[1.5rem]">
                    {lIdx + 1}.
                  </span>
                  <span>{line.replace(/^\d+[\.\)]\s*/, '')}</span>
                </li>
              ))}
            </ol>
          );
        }
        
        // Regular paragraph with line breaks preserved
        return (
          <p key={pIdx} className="text-gray-300 leading-relaxed">
            {lines.map((line, lIdx) => (
              <React.Fragment key={lIdx}>
                {line}
                {lIdx < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
};

const CourseDetailsPage = () => {
  const { courseSlug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Enrollment form state
  const [enrollForm, setEnrollForm] = useState({
    name: '',
    email: '',
    phone: '',
    college: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [selectedProjects, setSelectedProjects] = useState([]);
  
  // Coupon code state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  useEffect(() => {
    fetchCourseDetails();
  }, [courseSlug]);

  const fetchCourseDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/future-skills/courses/${courseSlug}`);
      setCourse(res.data);
    } catch (err) {
      console.error('Failed to fetch course:', err);
      setError(err.response?.data?.detail || 'Course not found');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!enrollForm.name.trim()) errors.name = 'Name is required';
    if (!enrollForm.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(enrollForm.email)) errors.email = 'Invalid email';
    if (!enrollForm.phone.trim()) errors.phone = 'Phone is required';
    else if (!/^\d{10}$/.test(enrollForm.phone.replace(/\D/g, ''))) errors.phone = 'Invalid phone number';
    
    // Project selection validation
    const minProjects = course?.min_projects_required || 2;
    if (course?.projects?.length > 0 && selectedProjects.length < minProjects) {
      errors.projects = `Please select at least ${minProjects} projects`;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    
    setCouponLoading(true);
    setCouponError('');
    
    try {
      const res = await axios.post(`${API}/future-skills/validate-coupon`, {
        course_id: course.id,
        coupon_code: couponCode.trim()
      });
      
      setAppliedCoupon(res.data);
      setCouponError('');
    } catch (err) {
      setCouponError(err.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setEnrolling(true);
    try {
      const res = await axios.post(`${API}/future-skills/enroll`, {
        course_id: course.id,
        name: enrollForm.name,
        email: enrollForm.email,
        phone: enrollForm.phone,
        college: enrollForm.college,
        coupon_code: appliedCoupon?.coupon_code || '',
        selected_projects: selectedProjects  // NEW: Include selected projects
      });
      
      // Check if this is a paid course with payment session
      if (res.data.payment_session_id) {
        // Use Cashfree SDK for payment
        try {
          const { load } = await import('@cashfreepayments/cashfree-js');
          const cashfree = await load({ mode: 'production' });
          await cashfree.checkout({
            paymentSessionId: res.data.payment_session_id,
            redirectTarget: '_self'
          });
        } catch (paymentErr) {
          console.error('Payment initialization failed:', paymentErr);
          setFormErrors({ submit: 'Payment initialization failed. Please try again.' });
        }
      } else if (res.data.status === 'confirmed') {
        // Free course or fully discounted - show success and redirect
        setShowEnrollModal(false);
        toast.success('Successfully enrolled! Redirecting to your purchases...');
        navigate(`/codearena/dashboard?tab=purchases`);
      } else if (res.data.enrollment_id) {
        // Already enrolled or other success state
        setShowEnrollModal(false);
        toast.success('You are already enrolled in this course!');
        navigate(`/codearena/dashboard?tab=purchases`);
      }
    } catch (err) {
      console.error('Enrollment failed:', err);
      setFormErrors({ submit: err.response?.data?.detail || 'Enrollment failed. Please try again.' });
    } finally {
      setEnrolling(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getCategoryColor = (category) => {
    const colors = {
      summer_internship: 'amber',
      placement_accelerator: 'purple',
      upskilling: 'cyan'
    };
    return colors[category] || 'cyan';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      summer_internship: Briefcase,
      placement_accelerator: Rocket,
      upskilling: TrendingUp
    };
    return icons[category] || GraduationCap;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading course details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto bg-red-500/10 rounded-2xl flex items-center justify-center mb-6">
            <AlertCircle className="text-red-400" size={40} />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Course Not Found</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <Link
            to="/future-skills/courses"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-medium"
          >
            <ArrowLeft size={18} />
            Browse All Courses
          </Link>
        </div>
      </div>
    );
  }

  const color = getCategoryColor(course.category);
  const CategoryIcon = getCategoryIcon(course.category);
  const seatsLeft = course.seats_left || course.max_seats;
  const isAlmostFull = seatsLeft && seatsLeft <= 10;

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      {/* Hero Section */}
      <section className="relative py-12 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className={`absolute top-0 left-1/4 w-[500px] h-[500px] bg-gradient-to-br from-${color}-500/15 to-${color}-600/5 rounded-full blur-3xl`} />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-gradient-to-br from-purple-500/10 to-cyan-500/5 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <Link to="/future-skills" className="hover:text-cyan-400 transition-colors">
              School of Future Skills
            </Link>
            <ChevronRight size={14} />
            <Link to="/future-skills/courses" className="hover:text-cyan-400 transition-colors">
              All Programs
            </Link>
            <ChevronRight size={14} />
            <span className="text-gray-300 truncate max-w-xs">{course.title}</span>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className={`inline-flex items-center gap-1 px-3 py-1 bg-${color}-500/20 text-${color}-400 rounded-full text-sm font-medium`}>
                  <CategoryIcon size={14} />
                  {course.category_name || course.category}
                </span>
                {course.is_featured && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium">
                    <Star size={14} />
                    Featured
                  </span>
                )}
                {course.mode && (
                  <span className="px-3 py-1 bg-white/10 text-gray-300 rounded-full text-sm">
                    {course.mode === 'online' ? '🌐 Online' : course.mode === 'offline' ? '🏢 Offline' : '🔄 Hybrid'}
                  </span>
                )}
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                {course.title}
              </h1>
              
              {course.description && (
                <div className="text-lg text-gray-400 mb-6 leading-relaxed max-w-3xl">
                  <FormattedText text={course.description} />
                </div>
              )}
              
              {/* Quick Stats */}
              <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                {course.duration_weeks && (
                  <span className="flex items-center gap-2">
                    <Clock className="text-cyan-400" size={18} />
                    {course.duration_weeks} weeks program
                  </span>
                )}
                {course.enrollment_count > 0 && (
                  <span className="flex items-center gap-2">
                    <Users className="text-purple-400" size={18} />
                    {course.enrollment_count}+ enrolled
                  </span>
                )}
                {course.venue && (
                  <span className="flex items-center gap-2">
                    <MapPin className="text-amber-400" size={18} />
                    {course.venue}
                  </span>
                )}
              </div>
              
              {/* Venue Details - Prominent display for offline courses */}
              {(course.mode === 'offline' || course.mode === 'hybrid') && (course.venue_name || course.venue_address) && (
                <div className="mt-6 p-5 bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/30 rounded-2xl" data-testid="venue-details-section">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-amber-400" size={24} />
                    </div>
                    <div>
                      <p className="text-amber-400 text-sm font-medium uppercase tracking-wide mb-1">
                        {course.mode === 'offline' ? '📍 In-Person Venue' : '📍 Offline Sessions Venue'}
                      </p>
                      {course.venue_name && (
                        <h3 className="text-xl font-semibold text-white mb-1">{course.venue_name}</h3>
                      )}
                      {course.venue_address && (
                        <p className="text-gray-400">{course.venue_address}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Mentor/Instructor Section - Only show if mentor details exist */}
              {course.mentor && course.mentor.name && (
                <div className="mt-6 p-5 bg-gradient-to-br from-cyan-500/10 to-blue-500/5 border border-cyan-500/30 rounded-2xl" data-testid="mentor-section">
                  <p className="text-cyan-400 text-sm font-medium uppercase tracking-wide mb-4">👨‍🏫 Your Instructor</p>
                  <div className="flex items-start gap-4">
                    {course.mentor.profile_image ? (
                      <img 
                        src={course.mentor.profile_image} 
                        alt={course.mentor.name}
                        className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                        {course.mentor.name.charAt(0)}
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-1">{course.mentor.name}</h3>
                      {course.mentor.experience && (
                        <p className="text-gray-400 text-sm mb-2">{course.mentor.experience}</p>
                      )}
                      {course.mentor.linkedin_url && (
                        <a 
                          href={course.mentor.linkedin_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-cyan-400 text-sm hover:text-cyan-300 transition-colors"
                        >
                          <ExternalLink size={14} />
                          View LinkedIn Profile
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Sidebar - Enrollment Card */}
            <div className="lg:col-span-1">
              <div className="sticky top-8 bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-6">
                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-4xl font-bold text-white">{formatPrice(course.price)}</span>
                    {course.original_price && course.original_price > course.price && (
                      <span className="text-lg text-gray-500 line-through">{formatPrice(course.original_price)}</span>
                    )}
                  </div>
                  {course.original_price && course.original_price > course.price && (
                    <span className="text-green-400 text-sm">
                      Save {Math.round((1 - course.price / course.original_price) * 100)}%
                    </span>
                  )}
                </div>
                
                {/* Key Details */}
                <div className="space-y-3 mb-6 pb-6 border-b border-white/10">
                  {course.start_date && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 flex items-center gap-2">
                        <Calendar size={16} />
                        Starts
                      </span>
                      <span className="text-white font-medium">
                        {new Date(course.start_date).toLocaleDateString('en-IN', { 
                          month: 'long', 
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                  {course.duration_weeks && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 flex items-center gap-2">
                        <Clock size={16} />
                        Duration
                      </span>
                      <span className="text-white font-medium">{course.duration_weeks} weeks</span>
                    </div>
                  )}
                  {seatsLeft && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 flex items-center gap-2">
                        <Users size={16} />
                        Seats Left
                      </span>
                      <span className={`font-medium ${isAlmostFull ? 'text-red-400' : 'text-white'}`}>
                        {seatsLeft} {isAlmostFull && '⚠️'}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* CTA Buttons */}
                <button
                  onClick={() => setShowEnrollModal(true)}
                  className="w-full py-4 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-semibold text-lg hover:shadow-lg hover:shadow-cyan-500/25 transition-all mb-3"
                  data-testid="enroll-now-btn"
                >
                  Enroll Now
                </button>
                
                <div className="flex gap-2">
                  <button
                    onClick={copyLink}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-white/5 border border-white/10 rounded-xl text-gray-300 hover:bg-white/10 transition-colors"
                    data-testid="copy-link-btn"
                  >
                    {copied ? <Check size={18} /> : <Copy size={18} />}
                    {copied ? 'Copied!' : 'Share'}
                  </button>
                </div>
                
                {/* Urgency Banner */}
                {isAlmostFull && (
                  <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-center">
                    <p className="text-red-400 text-sm font-medium">
                      🔥 Only {seatsLeft} seats remaining!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Tabs */}
      <section className="py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          {/* Tab Navigation */}
          <div className="flex gap-1 mb-8 border-b border-white/10 overflow-x-auto">
            {['overview', 'curriculum', 'instructors', 'faq'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                  activeTab === tab
                    ? 'text-cyan-400 border-cyan-400'
                    : 'text-gray-400 border-transparent hover:text-white'
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-8">
                  {/* What You'll Learn */}
                  {course.learning_outcomes && course.learning_outcomes.length > 0 && (
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <Target className="text-cyan-400" size={24} />
                        What You'll Learn
                      </h2>
                      <div className="grid md:grid-cols-2 gap-3">
                        {course.learning_outcomes.map((outcome, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-3 bg-white/5 rounded-xl">
                            <CheckCircle className="text-green-400 flex-shrink-0 mt-0.5" size={18} />
                            <span className="text-gray-300">{outcome}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Requirements */}
                  {course.requirements && course.requirements.length > 0 && (
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <Zap className="text-amber-400" size={24} />
                        Prerequisites
                      </h2>
                      <ul className="space-y-2">
                        {course.requirements.map((req, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-gray-300">
                            <span className="text-amber-400 mt-1.5">•</span>
                            {req}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Full Description */}
                  {course.full_description && (
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                        <BookOpen className="text-purple-400" size={24} />
                        About This Program
                      </h2>
                      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                        <FormattedText text={course.full_description} />
                      </div>
                    </div>
                  )}
                  
                  {/* Skills */}
                  {course.skills && course.skills.length > 0 && (
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-4">Skills You'll Gain</h2>
                      <div className="flex flex-wrap gap-2">
                        {course.skills.map((skill, idx) => (
                          <span key={idx} className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-gray-300">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* ============ NEW SECTIONS ============ */}
                  
                  {/* Impact Metrics Section */}
                  {course.show_impact_metrics !== false && (
                    <div className="relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-pink-500/10 rounded-3xl" />
                      <div className="relative p-8 border border-white/10 rounded-3xl">
                        <div className="text-center mb-8">
                          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                            Career Impact Metrics
                          </h2>
                          <p className="text-gray-400">Real outcomes from our alumni</p>
                        </div>
                        
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                          {/* Resume Shortlisting */}
                          <div className="text-center p-5 bg-white/5 rounded-2xl border border-white/10 hover:border-cyan-500/30 transition-all">
                            <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-green-400 mb-2">
                              {course.impact_metrics?.resume_shortlist_before || '12%'} → {course.impact_metrics?.resume_shortlist_after || '65%+'}
                            </div>
                            <div className="text-sm text-gray-400">Resume Shortlisting Rate</div>
                          </div>
                          
                          {/* Interview Conversion */}
                          <div className="text-center p-5 bg-white/5 rounded-2xl border border-white/10 hover:border-purple-500/30 transition-all">
                            <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-green-400 mb-2">
                              {course.impact_metrics?.interview_conversion_before || '8%'} → {course.impact_metrics?.interview_conversion_after || '45%+'}
                            </div>
                            <div className="text-sm text-gray-400">Interview Conversion</div>
                          </div>
                          
                          {/* Practical Experience */}
                          <div className="text-center p-5 bg-white/5 rounded-2xl border border-white/10 hover:border-amber-500/30 transition-all">
                            <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-green-400 mb-2">
                              {course.impact_metrics?.practical_experience_before || 'Low'} → {course.impact_metrics?.practical_experience_after || 'High'}
                            </div>
                            <div className="text-sm text-gray-400">Practical Experience</div>
                          </div>
                          
                          {/* Recruiter Response */}
                          <div className="text-center p-5 bg-white/5 rounded-2xl border border-white/10 hover:border-green-500/30 transition-all">
                            <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-400 to-green-400 mb-2">
                              {course.impact_metrics?.recruiter_response_before || 'Minimal'} → {course.impact_metrics?.recruiter_response_after || '3X'}
                            </div>
                            <div className="text-sm text-gray-400">Recruiter Response Rate</div>
                          </div>
                        </div>
                        
                        {/* Highlights */}
                        {course.highlights && course.highlights.length > 0 && (
                          <div className="mt-8 flex flex-wrap justify-center gap-4">
                            {course.highlights.map((highlight, idx) => (
                              <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full">
                                <CheckCircle className="text-green-400" size={16} />
                                <span className="text-green-300 text-sm">{highlight}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Industry Projects Section */}
                  {course.projects && course.projects.length > 0 && (
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
                          <Briefcase className="text-purple-400" size={20} />
                        </div>
                        Industry Projects
                      </h2>
                      <p className="text-gray-400 mb-6">Work on real-world projects during this program</p>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        {course.projects.map((project, idx) => (
                          <div key={idx} className="p-5 bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-2xl hover:border-purple-500/40 transition-all">
                            <h3 className="text-lg font-semibold text-white mb-2">{project.title}</h3>
                            {project.company && (
                              <div className="text-purple-400 text-sm mb-2 flex items-center gap-1">
                                <Building2 size={14} />
                                {project.company}
                              </div>
                            )}
                            {project.description && (
                              <p className="text-gray-400 text-sm">{project.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Industry Partners Section */}
                  {course.industry_partners && course.industry_partners.length > 0 && (
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
                        <div className="w-10 h-10 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                          <Award className="text-cyan-400" size={20} />
                        </div>
                        Industry Partners & Certifications
                      </h2>
                      <p className="text-gray-400 mb-6">Recognized by leading industry partners</p>
                      
                      <div className="flex flex-wrap gap-4">
                        {course.industry_partners.map((partner, idx) => (
                          <div key={idx} className="flex items-center gap-4 p-4 bg-white/5 border border-white/10 rounded-xl hover:border-cyan-500/30 transition-all">
                            {partner.logo_url && (
                              <img src={partner.logo_url} alt={partner.name} className="w-12 h-12 object-contain rounded-lg bg-white/10 p-2" />
                            )}
                            <div>
                              <h4 className="text-white font-semibold">{partner.name}</h4>
                              {partner.certification && (
                                <p className="text-cyan-400 text-sm">{partner.certification}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Certificates Preview Section */}
                  {(course.course_certificate?.title || course.confirmation_letter_preview) && (
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
                          <Award className="text-amber-400" size={20} />
                        </div>
                        What You'll Receive
                      </h2>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Course Certificate */}
                        {course.course_certificate?.title && (
                          <div className="p-6 bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 rounded-2xl">
                            <div className="flex items-center gap-3 mb-4">
                              <Award className="text-amber-400" size={24} />
                              <h3 className="text-lg font-semibold text-white">Course Certificate</h3>
                            </div>
                            <p className="text-gray-300 mb-4">{course.course_certificate.title}</p>
                            {course.course_certificate.preview_image && (
                              <img 
                                src={course.course_certificate.preview_image} 
                                alt="Certificate Preview" 
                                className="w-full rounded-lg border border-white/10 opacity-90 hover:opacity-100 transition-opacity"
                              />
                            )}
                          </div>
                        )}
                        
                        {/* Confirmation Letter */}
                        {course.confirmation_letter_preview && (
                          <div className="p-6 bg-gradient-to-br from-green-500/10 to-transparent border border-green-500/20 rounded-2xl">
                            <div className="flex items-center gap-3 mb-4">
                              <CheckCircle className="text-green-400" size={24} />
                              <h3 className="text-lg font-semibold text-white">Confirmation Letter</h3>
                            </div>
                            <p className="text-gray-300 mb-4">Official confirmation of your enrollment</p>
                            <img 
                              src={course.confirmation_letter_preview} 
                              alt="Confirmation Letter Preview" 
                              className="w-full rounded-lg border border-white/10 opacity-90 hover:opacity-100 transition-opacity"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Curriculum Tab */}
              {activeTab === 'curriculum' && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">Program Curriculum</h2>
                  {course.curriculum && course.curriculum.length > 0 ? (
                    <div className="space-y-4">
                      {course.curriculum.map((module, idx) => (
                        <div key={idx} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                          <div className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="w-8 h-8 bg-cyan-500/20 text-cyan-400 rounded-lg flex items-center justify-center font-semibold">
                                {idx + 1}
                              </span>
                              <div>
                                <h3 className="text-white font-medium">{module.title}</h3>
                                {module.duration && (
                                  <p className="text-gray-500 text-sm">{module.duration}</p>
                                )}
                              </div>
                            </div>
                          </div>
                          {module.topics && module.topics.length > 0 && (
                            <div className="px-4 pb-4 pt-2 border-t border-white/5">
                              <ul className="space-y-2">
                                {module.topics.map((topic, tIdx) => (
                                  <li key={tIdx} className="flex items-center gap-2 text-gray-400 text-sm">
                                    <Play size={12} className="text-cyan-400" />
                                    {topic}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-white/5 rounded-xl">
                      <BookOpen className="mx-auto text-gray-500 mb-3" size={40} />
                      <p className="text-gray-400">Detailed curriculum will be shared upon enrollment</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Instructors Tab */}
              {activeTab === 'instructors' && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">Meet Your Instructors</h2>
                  {course.instructors && course.instructors.length > 0 ? (
                    <div className="grid md:grid-cols-2 gap-6">
                      {course.instructors.map((instructor, idx) => (
                        <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-6">
                          <div className="flex items-start gap-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-xl flex items-center justify-center text-white text-xl font-bold">
                              {instructor.name?.charAt(0) || 'I'}
                            </div>
                            <div>
                              <h3 className="text-white font-semibold text-lg">{instructor.name}</h3>
                              <p className="text-cyan-400 text-sm">{instructor.title}</p>
                              {instructor.company && (
                                <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
                                  <Building2 size={12} />
                                  {instructor.company}
                                </p>
                              )}
                            </div>
                          </div>
                          {instructor.bio && (
                            <p className="text-gray-400 text-sm mt-4">{instructor.bio}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-white/5 rounded-xl">
                      <Users className="mx-auto text-gray-500 mb-3" size={40} />
                      <p className="text-gray-400">Industry experts from top companies</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* FAQ Tab */}
              {activeTab === 'faq' && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">Frequently Asked Questions</h2>
                  {course.faqs && course.faqs.length > 0 ? (
                    <div className="space-y-4">
                      {course.faqs.map((faq, idx) => (
                        <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-5">
                          <h3 className="text-white font-medium mb-2">{faq.question}</h3>
                          <p className="text-gray-400">{faq.answer}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <h3 className="text-white font-medium mb-2">How do I enroll in this program?</h3>
                        <p className="text-gray-400">Click the "Enroll Now" button and fill in your details. You'll receive a confirmation email with next steps.</p>
                      </div>
                      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <h3 className="text-white font-medium mb-2">What payment methods are accepted?</h3>
                        <p className="text-gray-400">We accept all major credit/debit cards, UPI, net banking, and popular wallets through our secure payment gateway.</p>
                      </div>
                      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <h3 className="text-white font-medium mb-2">Will I get a certificate?</h3>
                        <p className="text-gray-400">Yes, upon successful completion of the program, you'll receive a verified certificate that you can add to your LinkedIn profile.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Right Column - Additional Info */}
            <div className="lg:col-span-1 space-y-6">
              {/* Certificate Preview */}
              <div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-xl p-6">
                <Award className="text-amber-400 mb-3" size={32} />
                <h3 className="text-lg font-semibold text-white mb-2">Earn a Certificate</h3>
                <p className="text-gray-400 text-sm">
                  Get a verified certificate upon completion that you can share on LinkedIn and add to your resume.
                </p>
              </div>
              
              {/* Partner Badge */}
              {course.partner && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <Building2 className="text-cyan-400 mb-3" size={32} />
                  <p className="text-gray-500 text-sm mb-2">In partnership with</p>
                  <h3 className="text-xl font-semibold text-white">{course.partner}</h3>
                </div>
              )}
              
              {/* Related Courses Placeholder */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Need Help?</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Have questions about this program? Our counselors are here to help.
                </p>
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2 w-full py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20 transition-colors"
                >
                  Contact Us
                  <ExternalLink size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enrollment Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto relative">
            <div className="sticky top-0 bg-[#0a0a0a] p-6 pb-2 border-b border-white/10 z-10">
              <button
                onClick={() => setShowEnrollModal(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-white"
              >
                <X size={24} />
              </button>
              
              <h2 className="text-2xl font-bold text-white mb-2">Enroll in Program</h2>
              <p className="text-gray-400">{course.title}</p>
            </div>
            
            <form onSubmit={handleEnroll} className="space-y-4 p-6 pt-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={enrollForm.name}
                  onChange={(e) => setEnrollForm(f => ({ ...f, name: e.target.value }))}
                  className={`w-full px-4 py-3 bg-white/5 border ${formErrors.name ? 'border-red-500' : 'border-white/10'} rounded-xl text-white focus:outline-none focus:border-cyan-500/50`}
                  placeholder="Enter your full name"
                  data-testid="enroll-name-input"
                />
                {formErrors.name && <p className="text-red-400 text-sm mt-1">{formErrors.name}</p>}
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Email Address *</label>
                <input
                  type="email"
                  value={enrollForm.email}
                  onChange={(e) => setEnrollForm(f => ({ ...f, email: e.target.value }))}
                  className={`w-full px-4 py-3 bg-white/5 border ${formErrors.email ? 'border-red-500' : 'border-white/10'} rounded-xl text-white focus:outline-none focus:border-cyan-500/50`}
                  placeholder="your@email.com"
                  data-testid="enroll-email-input"
                />
                {formErrors.email && <p className="text-red-400 text-sm mt-1">{formErrors.email}</p>}
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  value={enrollForm.phone}
                  onChange={(e) => setEnrollForm(f => ({ ...f, phone: e.target.value }))}
                  className={`w-full px-4 py-3 bg-white/5 border ${formErrors.phone ? 'border-red-500' : 'border-white/10'} rounded-xl text-white focus:outline-none focus:border-cyan-500/50`}
                  placeholder="10-digit mobile number"
                  data-testid="enroll-phone-input"
                />
                {formErrors.phone && <p className="text-red-400 text-sm mt-1">{formErrors.phone}</p>}
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">College/University</label>
                <input
                  type="text"
                  value={enrollForm.college}
                  onChange={(e) => setEnrollForm(f => ({ ...f, college: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500/50"
                  placeholder="Your institution name"
                  data-testid="enroll-college-input"
                />
              </div>
              
              {formErrors.submit && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                  <p className="text-red-400 text-sm">{formErrors.submit}</p>
                </div>
              )}
              
              {/* Project Selection Section */}
              {course.projects && course.projects.length > 0 && (
                <div className="space-y-3">
                  <label className="block text-sm text-gray-400">
                    Select Projects * <span className="text-purple-400">(min {course.min_projects_required || 2} required)</span>
                  </label>
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                    {course.projects.map((project) => (
                      <label 
                        key={project.id || project.title} 
                        className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                          selectedProjects.includes(project.title)
                            ? 'bg-purple-500/20 border border-purple-500/30'
                            : 'bg-white/5 border border-white/10 hover:border-purple-500/30'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedProjects.includes(project.title)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedProjects([...selectedProjects, project.title]);
                            } else {
                              setSelectedProjects(selectedProjects.filter(p => p !== project.title));
                            }
                          }}
                          className="mt-1 w-4 h-4 rounded border-gray-600 text-purple-500 focus:ring-purple-500"
                        />
                        <div>
                          <p className="text-white font-medium text-sm">{project.title}</p>
                          {project.company && (
                            <p className="text-purple-400 text-xs">{project.company}</p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                  {formErrors.projects && <p className="text-red-400 text-sm">{formErrors.projects}</p>}
                  <p className="text-gray-500 text-xs">{selectedProjects.length} of {course.projects.length} selected</p>
                </div>
              )}
              
              {/* Coupon Code Section */}
              {course.coupons && course.coupons.length > 0 && course.price > 0 && (
                <div className="space-y-2">
                  <label className="block text-sm text-gray-400">Have a coupon code?</label>
                  {appliedCoupon ? (
                    <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-xl flex items-center justify-between">
                      <div>
                        <span className="text-green-400 font-medium">{appliedCoupon.coupon_code}</span>
                        <span className="text-green-400 ml-2">- {appliedCoupon.message}</span>
                      </div>
                      <button
                        type="button"
                        onClick={removeCoupon}
                        className="text-gray-400 hover:text-red-400 p-1"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
                        placeholder="Enter coupon code"
                        data-testid="coupon-input"
                      />
                      <button
                        type="button"
                        onClick={handleApplyCoupon}
                        disabled={couponLoading}
                        className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-xl font-medium hover:bg-purple-500/30 disabled:opacity-50"
                        data-testid="apply-coupon-btn"
                      >
                        {couponLoading ? 'Applying...' : 'Apply'}
                      </button>
                    </div>
                  )}
                  {couponError && <p className="text-red-400 text-sm">{couponError}</p>}
                </div>
              )}
              
              <div className="pt-4 border-t border-white/10">
                {/* Price Summary */}
                <div className="space-y-2 mb-4">
                  {appliedCoupon ? (
                    <>
                      <div className="flex items-center justify-between text-gray-400">
                        <span>Original Price</span>
                        <span className="line-through">{formatPrice(course.price)}</span>
                      </div>
                      <div className="flex items-center justify-between text-green-400">
                        <span>Discount ({appliedCoupon.coupon_code})</span>
                        <span>-{formatPrice(appliedCoupon.discount_amount)}</span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-white/10">
                        <span className="text-gray-400 font-medium">Amount to Pay</span>
                        <span className="text-2xl font-bold text-white">{formatPrice(appliedCoupon.final_price)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Amount to Pay</span>
                      <span className="text-2xl font-bold text-white">{formatPrice(course.price)}</span>
                    </div>
                  )}
                </div>
                
                <button
                  type="submit"
                  disabled={enrolling}
                  className="w-full py-4 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-semibold text-lg hover:shadow-lg hover:shadow-cyan-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="confirm-enroll-btn"
                >
                  {enrolling ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Processing...
                    </>
                  ) : (
                    <>
                      Proceed to Payment
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetailsPage;
